import { Alert, Button, Card, Form, Input, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSubjectSelect,
} from '@/pages/education/components/EducationLookupSelects';

type UnitRow = {
  id: string;
  code: string;
  name: string;
  sequence_no: number;
  is_active?: number | boolean;
  subject_code?: string;
  subject_name?: string;
  grade_code?: string;
  outcome_count?: number;
};

export default function CurriculumUnitsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.curriculum.manage') || user?.permissions?.includes('education.admin')
  );
  const [subjectId, setSubjectId] = useState<string | undefined>();
  const [gradeId, setGradeId] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-curriculum-units', subjectId, gradeId, q],
    queryFn: async () => {
      const res = await api.get<UnitRow[]>('/education/curriculum/units', {
        params: {
          subject_id: subjectId || undefined,
          grade_level_id: gradeId || undefined,
          q: q.trim() || undefined,
        },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('curriculumUnits')}
      description={t('curriculumUnitsIntro')}
      extra={
        <Space wrap>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/curriculum/units/new')}>
              {t('newCurriculumUnit')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/curriculum')}>{t('backToCurriculum')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline">
            <Form.Item label={t('subject')}>
              <EducationSubjectSelect
                allowClear
                style={{ minWidth: 200 }}
                value={subjectId}
                onChange={(v) => setSubjectId(v || undefined)}
              />
            </Form.Item>
            <Form.Item label={t('grade')}>
              <EducationGradeSelect
                allowClear
                style={{ minWidth: 160 }}
                value={gradeId}
                onChange={(v) => setGradeId(v || undefined)}
              />
            </Form.Item>
            <Form.Item label={t('search', { defaultValue: 'Search' })}>
              <Input
                allowClear
                style={{ minWidth: 180 }}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('curriculumSearchHint')}
              />
            </Form.Item>
          </Form>
        </Card>
        {list.isError ? (
          <Alert type="error" showIcon message={t('unavailable', { status: '' })} />
        ) : (
          <Card size="small">
            <Table
              rowKey="id"
              size="small"
              loading={list.isLoading}
              dataSource={list.data ?? []}
              pagination={{ pageSize: 25 }}
              locale={{ emptyText: t('curriculumUnitsEmpty') }}
              columns={[
                { title: t('sequence'), dataIndex: 'sequence_no', width: 80 },
                { title: t('code'), dataIndex: 'code', width: 100 },
                { title: t('name'), dataIndex: 'name' },
                {
                  title: t('subject'),
                  render: (_: unknown, row: UnitRow) =>
                    `${row.subject_code || ''} — ${row.subject_name || ''}`.trim(),
                },
                { title: t('grade'), dataIndex: 'grade_code', width: 80 },
                { title: t('outcomes'), dataIndex: 'outcome_count', width: 90 },
                {
                  title: t('status'),
                  width: 90,
                  render: (_: unknown, row: UnitRow) =>
                    row.is_active === 0 || row.is_active === false ? (
                      <Tag>{t('inactive')}</Tag>
                    ) : (
                      <Tag color="success">{t('active')}</Tag>
                    ),
                },
                {
                  title: t('actions'),
                  width: 100,
                  render: (_: unknown, row: UnitRow) => (
                    <Button size="small" onClick={() => navigate(`/education/curriculum/units/${row.id}`)}>
                      {t('open')}
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        )}
      </Space>
    </EducationPageChrome>
  );
}
